import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Filter, Download, RefreshCw, ChevronDown, ChevronLeft, ChevronRight,
  FileText, FileSpreadsheet, FileType2, Pencil, Send, Trash2, Eye,
  PackageCheck, CreditCard, Search, MoreVertical, ArrowUpDown,
  ArrowDown, ArrowUp, Bell, PhoneOff, RotateCcw, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import {
  listOrders, submitDraft, deleteOrder, exportOrders,
} from "@/lib/api";
import { fmtDateLong, fmtDateTimeLong, ROLE_LABELS } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// === Status definitions ===
const STATUS_DEFS = {
  draft: { label: "Draft", cls: "bg-[#e2e1eb] text-[#444653]", dot: "bg-[#747684]" },
  new_order: { label: "New", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  ordered: { label: "Ordered", cls: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  partially_received: { label: "Partially Received", cls: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  pending_pickup: { label: "Pending Pickup", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  invoiced: { label: "Completed", cls: "bg-green-800 text-white", dot: "bg-green-900" },
  cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  returned_to_stock: { label: "Returned", cls: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  customer_not_responding: { label: "CNR", cls: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  unclaimed: { label: "Unclaimed", cls: "bg-[#4b4d57] text-white", dot: "bg-[#4b4d57]" },
};

// Tabs (lifecycle stages). "delayed" is a derived view (ordered/partially_received with edd<today).
const TABS = [
  { key: "ALL", label: "All", filter: {} },
  { key: "draft", label: "Drafts", filter: { status: "draft" } },
  { key: "new_order", label: "New", filter: { status: "new_order" } },
  { key: "ordered", label: "Ordered", filter: { status: "ordered,partially_received" } },
  { key: "pending_pickup", label: "Pending Pickup", filter: { status: "pending_pickup" } },
  { key: "delayed", label: "Delayed", filter: { status: "delayed" } },
  { key: "invoiced", label: "Completed", filter: { status: "invoiced" } },
  { key: "cancelled", label: "Cancelled", filter: { status: "cancelled" } },
  { key: "returned_to_stock", label: "Returned", filter: { status: "returned_to_stock" } },
  { key: "unclaimed", label: "Unclaimed", filter: { status: "unclaimed" } },
];

const TAB_BADGE_CLS = {
  draft: "bg-[#9aa0ad] text-white",
  new_order: "bg-blue-500 text-white",
  ordered: "bg-orange-500 text-white",
  pending_pickup: "bg-emerald-500 text-white",
  delayed: "bg-rose-600 text-white",
  invoiced: "bg-green-800 text-white",
  cancelled: "bg-rose-500 text-white",
  returned_to_stock: "bg-purple-500 text-white",
  unclaimed: "bg-[#4b4d57] text-white",
};

// Abbreviate "Ravi Kumar" -> "Ravi K." ; "Anil" -> "Anil"
const shortName = (full) => {
  if (!full) return "—";
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

// Status indicator dot beside Order #
const orderDot = (status, isDelayed) => {
  if (isDelayed) return "bg-rose-500"; // 🔴 Delayed
  if (status === "returned_to_stock") return "bg-purple-500"; // 🟣 Returned
  if (status === "unclaimed") return "bg-[#4b4d57]"; // Unclaimed — dark grey
  if (status === "invoiced") return "bg-green-700"; // Completed — dark green
  if (status === "cancelled") return "bg-rose-300";
  if (status === "draft") return "bg-[#9aa0ad]";
  return "bg-emerald-500"; // 🟢 Active (new/ordered/pp/etc)
};

const PAGE_SIZE = 20;

// === Date presets ===
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const lastMonthRange = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const end = new Date(d.getFullYear(), d.getMonth(), 0);
  const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return { from: fmt(start), to: fmt(end) };
};
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "month", label: "Current Month" },
  { key: "lastmonth", label: "Last Month" },
  { key: "all", label: "All Time" },
  { key: "custom", label: "Custom Range" },
];

const computePreset = (fromD, toD) => {
  // "All Time" = no date restriction at all.
  if (!fromD && !toD) return "all";
  const t = todayISO();
  if (fromD === t && toD === t) return "today";
  if (fromD === daysAgoISO(7) && toD === t) return "7d";
  if (fromD === monthStartISO() && toD === t) return "month";
  const lm = lastMonthRange();
  if (fromD === lm.from && toD === lm.to) return "lastmonth";
  return "custom";
};

const AllOrders = () => {
  const { currentUser, currentLocation, locations, users } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const initialQ = searchParams.get("q") || "";
  const validTabKeys = TABS.map((t) => t.key);

  // Default: Current Month
  const [filterFromDate, setFilterFromDate] = useState(monthStartISO());
  const [filterToDate, setFilterToDate] = useState(todayISO());
  const [datePreset, setDatePreset] = useState("month");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(validTabKeys.includes(initialTab) ? initialTab : "ALL");
  const [q, setQ] = useState(initialQ);
  const [filterLocation, setFilterLocation] = useState("ALL");
  const [filterOrderType, setFilterOrderType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterCreatedBy, setFilterCreatedBy] = useState("ALL");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [exportOpen, setExportOpen] = useState(false);

  const effectiveLocation = filterLocation !== "ALL" ? filterLocation : currentLocation;

  // Build query params: tab + filters
  const buildParams = (forExport = false) => {
    const params = { include_drafts: true, view: "active" };
    // Tab status takes precedence; if "ALL", honor filterStatus dropdown
    const tabDef = TABS.find((t) => t.key === tab);
    if (tabDef && tabDef.key !== "ALL") {
      Object.assign(params, tabDef.filter);
    } else if (filterStatus !== "ALL") {
      params.status = filterStatus;
    }
    if (effectiveLocation && effectiveLocation !== "ALL") params.location_code = effectiveLocation;
    if (q) params.q = q;
    if (filterOrderType !== "ALL") params.order_against = filterOrderType;
    if (filterCreatedBy !== "ALL") params.created_by = filterCreatedBy;
    if (filterFromDate) params.from_date = filterFromDate;
    if (filterToDate) params.to_date = filterToDate;
    if (!forExport) {
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
    }
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const list = await listOrders(buildParams());
      setOrders(list);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  // Keep `q` in sync with the URL query — lets the global header search bar
  // (which navigates with `?q=`) update the in-page search even when the user
  // is already on /orders.
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab, currentLocation, filterLocation, q, filterOrderType, filterStatus, filterCreatedBy,
      filterFromDate, filterToDate, sortBy, sortDir]);

  // Apply date preset
  const applyPreset = (key) => {
    setDatePreset(key);
    const t = todayISO();
    if (key === "today") { setFilterFromDate(t); setFilterToDate(t); }
    else if (key === "7d") { setFilterFromDate(daysAgoISO(7)); setFilterToDate(t); }
    else if (key === "month") { setFilterFromDate(monthStartISO()); setFilterToDate(t); }
    else if (key === "lastmonth") {
      const { from, to } = lastMonthRange();
      setFilterFromDate(from); setFilterToDate(to);
    }
    else if (key === "all") { setFilterFromDate(""); setFilterToDate(""); }
    // custom: leave dates as-is
  };

  // Keep preset display in sync if dates manually edited
  useEffect(() => {
    setDatePreset(computePreset(filterFromDate, filterToDate));
  }, [filterFromDate, filterToDate]);

  const handleExport = async (format) => {
    setExportOpen(false);
    try {
      const resp = await exportOrders({ ...buildParams(true), format });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `IVA_ORDERS.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const label = { csv: "CSV", xlsx: "Excel", xlsx_detailed: "Detailed Excel", pdf: "PDF Detailed", pdf_summary: "PDF Summary" }[format] || format;
      toast.success(`Exported ${orders.length} order(s) as ${label}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const onSubmitDraft = async (id) => {
    if (!window.confirm("Submit this draft as a New Order? It will move to the Workbench.")) return;
    try {
      await submitDraft(id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Draft submitted as New Order");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this order? This action is logged.")) return;
    try {
      await deleteOrder(id, currentUser.name, currentUser.role);
      toast.success("Order deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };

  const goToWorkbench = (o, wbTab, extra = {}) => {
    const params = new URLSearchParams({ filter: wbTab, q: o.order_number, ...extra });
    navigate(`/workbench?${params.toString()}`);
  };

  // === KPI strip (computed client-side from loaded orders) ===
  const kpis = useMemo(() => {
    const total = orders.length;
    const drafts = orders.filter((o) => o.status === "draft").length;
    const completed = orders.filter((o) => o.status === "invoiced").length;
    const cancelled = orders.filter((o) => o.status === "cancelled").length;
    const pending = orders.filter((o) =>
      ["new_order", "ordered", "partially_received", "pending_pickup"].includes(o.status)
    ).length;
    const today = new Date().toISOString().slice(0, 10);
    const delayed = orders.filter((o) =>
      ["ordered", "partially_received"].includes(o.status) && o.edd && o.edd < today
    ).length;
    const leads = orders
      .map((o) => o.lead_time_days)
      .filter((x) => typeof x === "number");
    const avgLead = leads.length ? (leads.reduce((a, b) => a + b, 0) / leads.length).toFixed(1) : "0";
    return { drafts, total, completed, cancelled, pending, delayed, avgLead };
  }, [orders]);

  // === Tab counts (client-side from loaded orders) ===
  const counts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const c = { ALL: orders.length };
    TABS.forEach((t) => {
      if (t.key === "ALL") return;
      if (t.key === "delayed") {
        c.delayed = orders.filter((o) =>
          ["ordered", "partially_received"].includes(o.status) && o.edd && o.edd < today
        ).length;
      } else if (t.key === "ordered") {
        c.ordered = orders.filter((o) => ["ordered", "partially_received"].includes(o.status)).length;
      } else {
        c[t.key] = orders.filter((o) => o.status === t.key).length;
      }
    });
    return c;
  }, [orders]);

  // === Apply tab status filter client-side (so counts and view stay consistent without refetch) ===
  const filteredOrders = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (tab === "ALL") return orders;
    if (tab === "delayed") {
      return orders.filter((o) =>
        ["ordered", "partially_received"].includes(o.status) && o.edd && o.edd < today
      );
    }
    if (tab === "ordered") {
      return orders.filter((o) => ["ordered", "partially_received"].includes(o.status));
    }
    return orders.filter((o) => o.status === tab);
  }, [orders, tab]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pagedOrders = useMemo(
    () => filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredOrders, page]
  );
  useEffect(() => { setPage(1); }, [tab, filteredOrders.length]);

  const reset = () => {
    setQ("");
    setFilterLocation("ALL");
    setFilterOrderType("ALL");
    setFilterStatus("ALL");
    setFilterCreatedBy("ALL");
    setFilterFromDate(monthStartISO());
    setFilterToDate(todayISO());
  };

  const hasNonDefaultFilters = q || filterLocation !== "ALL" || filterOrderType !== "ALL" ||
    filterStatus !== "ALL" || filterCreatedBy !== "ALL" || datePreset !== "month";

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-yamaha-700" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-yamaha-700" />;
  };

  return (
    <div className="space-y-4" data-testid="all-orders-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-semibold text-yamaha-800 leading-tight tracking-tight">All Orders</h1>
          <p className="text-[13px] text-[#444653] mt-0.5">
            Single master view across the complete order lifecycle. Default: current month + active orders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can(currentUser, "orders_export") && (
          <DropdownMenu open={exportOpen} onOpenChange={setExportOpen}>
            <DropdownMenuTrigger
              data-testid="export-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold"
            >
              <Download className="w-3.5 h-3.5" />
              Export
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid="export-xlsx">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx_detailed")} data-testid="export-xlsx-detailed">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-2 text-emerald-600" />
                <div className="flex flex-col leading-tight">
                  <span>Detailed Excel Report</span>
                  <span className="text-[10.5px] text-[#747684]">One row per part line item</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv">
                <FileText className="w-3.5 h-3.5 mr-2" />CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport("pdf_summary")} data-testid="export-pdf-summary">
                <FileType2 className="w-3.5 h-3.5 mr-2" />PDF Summary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf-detailed">
                <FileType2 className="w-3.5 h-3.5 mr-2" />PDF Detailed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI strip — minimal height */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2" data-testid="kpi-strip">
        <KPI testid="kpi-total" label="Total Orders" value={kpis.total} onClick={() => setTab("ALL")} />
        <KPI testid="kpi-pending" label="Pending" value={kpis.pending} accent="amber" />
        <KPI testid="kpi-completed" label="Completed" value={kpis.completed} accent="emerald" onClick={() => setTab("invoiced")} />
        <KPI testid="kpi-delayed" label="Delayed" value={kpis.delayed} accent="rose" onClick={() => setTab("delayed")} />
        <KPI testid="kpi-cancelled" label="Cancelled" value={kpis.cancelled} accent="rose" onClick={() => setTab("cancelled")} />
        <KPI testid="kpi-leadtime" label="Average Lead Time" value={`${kpis.avgLead}d`} accent="blue" />
        <KPI
          testid="kpi-drafts"
          label="Draft Orders"
          value={kpis.drafts}
          highlight
          subtle="Awaiting Completion"
          onClick={() => setTab("draft")}
        />
      </div>

      {/* Main card */}
      <div className="bg-white rounded-xl border border-[#e2e1eb]">
        {/* Filters */}
        <div className="px-4 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] space-y-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-1.5 text-[#747684]">
              <Filter className="w-3.5 h-3.5" />
              <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="search-input"
                placeholder="Customer, mobile, order #, VIN, part #..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="input w-full pl-8 !py-1.5 !text-[12px]"
              />
            </div>
            <select
              data-testid="filter-location"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
              title="Location"
            >
              <option value="ALL">All Locations</option>
              {locations.map((l) => (
                <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
              ))}
            </select>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              disabled={tab !== "ALL"}
              className="input !w-auto !py-1.5 !text-[12px] disabled:opacity-50"
              title={tab !== "ALL" ? "Active tab overrides status filter" : "Status"}
            >
              <option value="ALL">All Status</option>
              <option value="draft">Draft</option>
              <option value="new_order">New</option>
              <option value="ordered">Ordered</option>
              <option value="partially_received">Partially Received</option>
              <option value="pending_pickup">Pending Pickup</option>
              <option value="invoiced">Completed</option>
              <option value="returned_to_stock">Returned</option>
              <option value="customer_not_responding">CNR</option>
              <option value="unclaimed">Unclaimed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              data-testid="filter-order-type"
              value={filterOrderType}
              onChange={(e) => setFilterOrderType(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
              title="Order Type"
            >
              <option value="ALL">All Order Types</option>
              <option value="job_card">Job Card</option>
              <option value="counter_sale">Counter Sale</option>
            </select>
            <select
              data-testid="filter-created-by"
              value={filterCreatedBy}
              onChange={(e) => setFilterCreatedBy(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
              title="Created By"
            >
              <option value="ALL">All Users</option>
              {users.map((u) => (
                <option key={u.id || u.name} value={u.name}>{u.name}</option>
              ))}
            </select>
            {hasNonDefaultFilters && (
              <button
                data-testid="reset-filters"
                onClick={reset}
                className="text-[12px] text-yamaha-700 font-semibold hover:underline"
              >
                Reset
              </button>
            )}
          </div>

          {/* Date preset row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#747684]">Date Range:</span>
            <div className="flex items-center gap-1" data-testid="date-presets">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`preset-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                    datePreset === p.key
                      ? "bg-yamaha-700 text-white border-yamaha-700"
                      : "bg-white text-[#444653] border-[#e2e1eb] hover:border-yamaha-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {(datePreset === "custom" || true) && (
              <div className="flex items-center gap-1.5 ml-1">
                <input
                  data-testid="filter-from-date"
                  type="date"
                  value={filterFromDate}
                  onChange={(e) => setFilterFromDate(e.target.value)}
                  className="input !w-36 !py-1 !text-[11px]"
                />
                <span className="text-[#747684] text-[11px]">to</span>
                <input
                  data-testid="filter-to-date"
                  type="date"
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                  className="input !w-36 !py-1 !text-[11px]"
                />
              </div>
            )}
          </div>
        </div>

        {/* Status tabs with counts */}
        <div className="flex overflow-x-auto border-b border-[#e2e1eb] px-2" data-testid="status-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const c = counts[t.key] ?? 0;
            const colorCls = TAB_BADGE_CLS[t.key];
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[12px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-yamaha-700 text-yamaha-700"
                    : "border-transparent text-[#444653] hover:text-yamaha-700"
                }`}
              >
                {t.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  colorCls && c > 0
                    ? colorCls
                    : active
                    ? "bg-yamaha-700 text-white"
                    : "bg-[#e2e1eb] text-[#444653]"
                }`}>
                  {String(c).padStart(2, "0")}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-testid="orders-table-wrap">
          <table className="w-full text-[12px]" data-testid="all-orders-table">
            <thead>
              <tr className="bg-[#fafbff] border-b border-[#e2e1eb] text-[10px] uppercase tracking-wider text-[#747684]">
                <Th sortable field="order_number" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Order #</Th>
                <Th sortable field="customer_name" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Customer</Th>
                <Th>Vehicle</Th>
                <Th sortable field="location_code" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Location</Th>
                <Th>Type</Th>
                <Th sortable field="status" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Status</Th>
                <Th align="center">Parts</Th>
                <Th sortable field="created_at" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Created</Th>
                <Th sortable field="updated_at" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Last Updated</Th>
                <Th>Assigned User</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="orders-tbody">
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-[#747684]">Loading…</td></tr>
              ) : pagedOrders.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-[#747684]" data-testid="empty-state">
                  No orders match the current filters.
                </td></tr>
              ) : (
                pagedOrders.map((o) => {
                  const sb = STATUS_DEFS[o.status] || { label: o.status, cls: "bg-[#e2e1eb] text-[#444653]" };
                  const totalParts = o.total_parts || (o.parts || []).reduce((s, p) => s + (p.qty || 0), 0);
                  const today = new Date().toISOString().slice(0, 10);
                  const isDelayed = ["ordered", "partially_received"].includes(o.status) && o.edd && o.edd < today;
                  const effectiveSb = isDelayed
                    ? { label: "Delayed", cls: "bg-rose-100 text-rose-700" }
                    : sb;
                  return (
                    <tr
                      key={o.id}
                      data-testid={`row-${o.order_number}`}
                      className="border-b border-[#f1f0f7] hover:bg-[#faf8ff] transition-colors"
                      style={{ height: 44 }}
                    >
                      <td className="px-4 py-2 font-mono font-semibold text-yamaha-700 whitespace-nowrap">
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${orderDot(o.status, isDelayed)}`}
                          title={isDelayed ? "Delayed" : effectiveSb.label}
                          data-testid={`dot-${o.order_number}`}
                        />
                        {o.order_number}
                      </td>
                      <td className="px-4 py-2 font-semibold text-[#1a1b22] truncate max-w-[150px]" title={o.customer_name}>{o.customer_name}</td>
                      <td className="px-4 py-2 text-[#444653] truncate max-w-[160px]" title={o.model_name}>{o.model_name}</td>
                      <td className="px-4 py-2 text-[#444653]">{o.location_code}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                          o.order_against === "job_card" ? "bg-emerald-50 text-emerald-700" : "bg-yamaha-50 text-yamaha-700"
                        }`}>
                          {o.order_against === "job_card" ? "Job Card" : "Counter"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${effectiveSb.cls}`}
                          data-testid={`status-${o.order_number}`}
                        >
                          {effectiveSb.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center font-semibold text-[#1a1b22]">{totalParts}</td>
                      <td className="px-4 py-2 text-[#444653] whitespace-nowrap">{fmtDateLong(o.created_at)}</td>
                      <td
                        className="px-4 py-2 text-[#747684] whitespace-nowrap text-[11px]"
                        title={o.updated_by ? `Updated by ${o.updated_by}${o.updated_by_role ? ` (${ROLE_LABELS[o.updated_by_role] || o.updated_by_role})` : ""}` : ""}
                      >
                        <div>{fmtDateTimeLong(o.updated_at)}</div>
                        {o.updated_by ? (
                          <div className="text-[10px] text-[#444653] mt-0.5" data-testid={`updated-by-${o.order_number}`}>
                            by <span className="font-medium">{shortName(o.updated_by)}</span>
                            {o.updated_by_role ? <span className="text-[#747684]"> · {ROLE_LABELS[o.updated_by_role] || o.updated_by_role}</span> : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-[#444653] whitespace-nowrap" title={o.created_by ? `${o.created_by}${o.created_by_role ? ` (${ROLE_LABELS[o.created_by_role] || o.created_by_role})` : ""}` : "—"}>
                        <div>{shortName(o.created_by)}</div>
                        {o.created_by_role ? (
                          <div className="text-[10px] text-[#747684]">{ROLE_LABELS[o.created_by_role] || o.created_by_role}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <RowMenu
                          order={o}
                          isDelayed={isDelayed}
                          onEditDraft={() => navigate(`/create?edit=${o.id}`)}
                          onSubmitDraft={() => onSubmitDraft(o.id)}
                          onDelete={() => onDelete(o.id)}
                          onMarkOrdered={() => goToWorkbench(o, "new_order")}
                          onReceive={() => goToWorkbench(o, isDelayed ? "delayed" : "ordered")}
                          onInvoice={() => goToWorkbench(o, "pending_pickup")}
                          onReminder={() => goToWorkbench(o, "pending_pickup")}
                          onCNR={() => goToWorkbench(o, "pending_pickup")}
                          onReturn={() => goToWorkbench(o, "unclaimed")}
                          onCancel={() => {
                            // Route to workbench tab matching status; cancel drawer auto-opens there
                            const tab = o.status === "new_order" ? "new_order" : (isDelayed ? "delayed" : "ordered");
                            goToWorkbench(o, tab, { cancel: o.id });
                          }}
                          onView={() => {
                            // Map status -> workbench tab
                            const map = {
                              new_order: "new_order", ordered: "ordered", partially_received: "ordered",
                              pending_pickup: "pending_pickup", invoiced: "completed",
                              returned_to_stock: "completed", unclaimed: "unclaimed",
                              customer_not_responding: "pending_pickup",
                            };
                            goToWorkbench(o, map[o.status] || "new_order");
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredOrders.length > 0 && (
          <div className="px-4 py-2.5 flex items-center justify-between text-[11.5px] text-[#747684] border-t border-[#e2e1eb]" data-testid="pagination">
            <div>Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length}</div>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="page-prev"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded hover:bg-[#f4f3fc] disabled:opacity-40"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="font-semibold text-[#444653]">{page} / {totalPages}</span>
              <button
                data-testid="page-next"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1 rounded hover:bg-[#f4f3fc] disabled:opacity-40"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// === KPI tile ===
const KPI = ({ testid, label, value, highlight, subtle, accent, onClick }) => {
  const accentColors = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    blue: "text-blue-700",
  };
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border bg-white flex items-center justify-between gap-2 ${onClick ? "cursor-pointer hover:shadow-sm" : ""} ${
        highlight ? "border-amber-400 bg-amber-50" : "border-[#e2e1eb]"
      }`}
      style={{ minHeight: 50 }}
    >
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#747684] truncate">{label}</div>
        {subtle && (
          <div className={`text-[9px] mt-0.5 font-semibold uppercase tracking-wider ${highlight ? "text-amber-700" : "text-[#747684]"}`}>{subtle}</div>
        )}
      </div>
      <div className={`text-[22px] font-semibold leading-none ${
        highlight ? "text-amber-700" : accentColors[accent] || "text-[#1a1b22]"
      }`}>{value}</div>
    </div>
  );
};

// === Th cell ===
const Th = ({ children, align = "left", sortable, field, sortBy, sortDir, onToggle }) => {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  if (!sortable) {
    return <th className={`px-4 py-2 font-bold ${alignCls}`}>{children}</th>;
  }
  const isActive = sortBy === field;
  return (
    <th className={`px-4 py-2 font-bold ${alignCls}`}>
      <button
        data-testid={`sort-${field}`}
        onClick={() => onToggle(field)}
        className={`inline-flex items-center hover:text-yamaha-700 ${isActive ? "text-yamaha-700" : ""}`}
      >
        {children}
        {isActive
          ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />)
          : <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />
        }
      </button>
    </th>
  );
};

// === Per-status 3-dot Actions menu ===
const RowMenu = ({
  order, isDelayed,
  onEditDraft, onSubmitDraft, onDelete,
  onMarkOrdered, onReceive, onInvoice, onReminder, onCNR, onReturn, onView, onCancel,
}) => {
  const s = order.status;
  const items = [];
  if (s === "draft") {
    items.push({ key: "edit", label: "Edit Draft", icon: Pencil, fn: onEditDraft, testid: "edit" });
    items.push({ key: "submit", label: "Submit", icon: Send, fn: onSubmitDraft, testid: "submit" });
    items.push({ key: "del", label: "Delete", icon: Trash2, fn: onDelete, danger: true, testid: "delete" });
  } else if (s === "new_order") {
    items.push({ key: "mo", label: "Mark Ordered", icon: PackageCheck, fn: onMarkOrdered, testid: "mark-ordered" });
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
    items.push({ key: "cancel", label: "Cancel Order", icon: XCircle, fn: onCancel, danger: true, testid: "cancel" });
  } else if (s === "ordered") {
    items.push({ key: "recv", label: isDelayed ? "Update / Receive" : "Receive Parts", icon: PackageCheck, fn: onReceive, testid: "receive" });
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
    items.push({ key: "cancel", label: "Cancel Order", icon: XCircle, fn: onCancel, danger: true, testid: "cancel" });
  } else if (s === "partially_received") {
    items.push({ key: "recv", label: isDelayed ? "Update / Receive" : "Receive Parts", icon: PackageCheck, fn: onReceive, testid: "receive" });
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
  } else if (s === "pending_pickup") {
    items.push({ key: "inv", label: "Invoice / Complete", icon: CreditCard, fn: onInvoice, testid: "invoice" });
    items.push({ key: "rem", label: "Send Reminder", icon: Bell, fn: onReminder, testid: "reminder" });
    items.push({ key: "cnr", label: "Log Follow-up", icon: PhoneOff, fn: onCNR, testid: "cnr" });
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
  } else if (s === "unclaimed") {
    items.push({ key: "ret", label: "Return To Stock", icon: RotateCcw, fn: onReturn, testid: "return" });
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
  } else {
    // invoiced / returned_to_stock / cancelled / cnr
    items.push({ key: "view", label: "View Details", icon: Eye, fn: onView, testid: "view" });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`row-menu-${order.order_number}`}
        className="p-1.5 rounded hover:bg-[#f4f3fc] text-[#444653] inline-flex"
      >
        <MoreVertical className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {items.map((it, i) => (
          <React.Fragment key={it.key}>
            {it.danger && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={it.fn}
              className={it.danger ? "text-red-600 focus:text-red-700" : ""}
              data-testid={`act-${it.testid}-${order.order_number}`}
            >
              <it.icon className="w-3.5 h-3.5 mr-2" />{it.label}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AllOrders;
